<template>
  <div>
    <v-dialog v-model="dialog" :key="key" max-width="600">
      <ManageStockEntry
        :stockItemId="stockItemId"
        :stockItemUnit="stockItemUnit"
        :institutionId="institutionId"
        :entryProp="entry"
        @close="dialog = false"
        @confirm="list(), (dialog = false), $emit('update')"
      />
    </v-dialog>

    <v-dialog v-model="attachDialog" :key="attachKey" max-width="600">
      <Attachments
        :stockEntryId="entry ? entry.id : ''"
        @close="attachDialog = false"
      />
    </v-dialog>

    <div class="d-flex align-center justify-space-between ma-4">
      <h3>{{ $t('Entradas') }}</h3>

      <div>
        <v-btn
          class="primary-button mr-2"
          depressed
          @click=";(entry = null), (key = !key), (dialog = true)"
        >
          {{ $t('Nova entrada') }}
        </v-btn>

        <v-btn class="secondary-button" depressed @click="show = !show">
          {{ show ? $t('Fechar') : $t('Filtros') }}
        </v-btn>
      </div>
    </div>

    <v-expand-transition>
      <div v-show="show">
        <v-form ref="form">
          <v-row class="ma-0" align="center" justify="end">
            <v-col>
              <span class="text ml-1">{{ $t('Data inicial') }}</span>
              <v-menu
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    class="primary-input"
                    placeholder="00/00/0000"
                    :value="formatDate(filter.start)"
                    :rules="
                      filter.final
                        ? [
                            () =>
                              filter.start <= filter.final || 'Data inválida',
                          ]
                        : []
                    "
                    outlined
                    readonly
                    v-bind="attrs"
                    hide-details="auto"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker locale="pt" v-model="filter.start">
                </v-date-picker>
              </v-menu>
            </v-col>

            <v-col>
              <span class="text ml-1">{{ $t('Data final') }}</span>
              <v-menu
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    class="primary-input"
                    placeholder="00/00/0000"
                    :value="formatDate(filter.final)"
                    :rules="
                      filter.start
                        ? [
                            () =>
                              filter.final >= filter.start || 'Data inválida',
                          ]
                        : []
                    "
                    outlined
                    readonly
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker locale="pt" v-model="filter.final">
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="auto">
              <div class="mt-3">
                <v-btn class="clear-button mr-2" depressed @click="reset">
                  {{ $t('Limpar') }}
                </v-btn>
                <v-btn @click="applyFilter" class="secondary-button" depressed>
                  {{ $t('Aplicar filtros') }}
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </div>
    </v-expand-transition>

    <v-data-table
      :headers="headers"
      :items="entries"
      :items-per-page="filter.pageSize"
      class="main-table ma-2"
      hide-default-footer
      hide-default-header
    >
      <template #header="{ props, on }">
        <thead>
          <tr>
            <th
              class="text-center"
              v-for="header in props.headers"
              :key="header.value"
            >
              <v-simple-checkbox
                v-if="header.value === 'data-table-select'"
                v-model="props.everyItem"
                :indeterminate="props.someItems && !props.everyItem"
                color="purple"
                @input="on['toggle-select-all']"
              ></v-simple-checkbox>

              <div>
                <v-hover v-slot="{ hover }">
                  <div>
                    <span>{{ $t(header.text) }}</span>
                    <v-icon
                      v-if="
                        hover &&
                        header.value != 'data-table-select' &&
                        header.value != 'actions'
                      "
                      color="white"
                      class="mx-2"
                      @click="on.sort(header.value)"
                    >
                      {{
                        props.options.sortBy.includes(header.value)
                          ? `mdi-chevron-down`
                          : `mdi-chevron-up`
                      }}
                    </v-icon>
                  </div>
                </v-hover>
              </div>
            </th>
          </tr>
        </thead>
      </template>

      <template #[`item.entryDate`]="{ item }">
        <span> {{ formatDate(item.entryDate) }} </span>
      </template>

      <template #[`item.nf`]="{ item }">
        <span> {{ item.nf ? item.nf : $t('Sem registros') }} </span>
      </template>

      <template #[`item.unitaryValue`]="{ item }">
        <span> {{ formatCurrency(item.unitaryValue) }} </span>
      </template>

      <template #[`item.totalValue`]="{ item }">
        <span> {{ formatCurrency(item.totalValue) }} </span>
      </template>

      <template #[`item.currentTotalValue`]="{ item }">
        <span> {{ formatCurrency(item.currentTotalValue) }} </span>
      </template>

      <template #[`item.reason`]="{ item }">
        <span> {{ item.reason ? item.reason : '-' }} </span>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              class="edit-button mr-2"
              icon
              @click="edit(item)"
              text
              v-on="on"
            >
              <v-icon size="22">mdi-pencil-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Editar') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              @click="attach(item)"
              class="edit-button mr-2"
              icon
              text
              v-on="on"
            >
              <v-icon size="22"> mdi-attachment </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Anexos') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <div class="pagination mx-2">
      <div class="item-pagination block-one">
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="btn"
              depressed
              color="#C0C0C0"
              small
            >
              qtd: {{ filter.pageSize }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(size, i) in sizes"
              :key="i"
              @click="filter.pageSize = size"
            >
              <v-list-item-title>{{ size }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <div class="registros" v-if="entries.length">
          <span> 1 - {{ entries.length }} de {{ filter.totalItems }} </span>
          <br />
          <span>
            {{ $t('Selecionados') }}:
            {{ entries.length }}
          </span>
        </div>
      </div>

      <div class="item-pagination block-two">
        <v-pagination
          class="pagination-item"
          v-model="filter.page"
          :length="filter.totalPages"
          :total-visible="5"
        ></v-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from '../../../../services/ApiService'
import Attachments from './Attachs.vue'
import ManageStockEntry from './ManageStockEntry.vue'
import FormatDate from '../../../../Utils/FormatDate'

export default {
  name: 'ManageEntries',

  props: {
    stockItemId: Number,
    stockItemUnit: String,
    institutionId: Number,
  },

  components: {
    ManageStockEntry,
    Attachments,
  },

  created() {
    this.list()
  },

  data() {
    return {
      apiService: new ApiService(),
      key: false,
      attachKey: 0,
      attachDialog: false,
      dialog: false,
      show: false,
      entry: {},
      entries: [],
      sizes: [10, 30, 90, 180],
      filter: {
        start: '',
        final: '',
        stockItemId: null,
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
      },
      headers: [
        {
          text: '',
          align: 'center',
          value: '',
          sortable: false,
        },
        {
          text: 'Fornecedor',
          align: 'center',
          value: 'supplier.name',
          sortable: false,
        },
        {
          text: 'Quantidade inicial',
          align: 'center',
          value: 'enteredQuantity',
        },
        {
          text: 'Quantidade atual',
          align: 'center',
          value: 'currentQuantity',
        },
        {
          text: 'Valor unitário',
          align: 'center',
          value: 'unitaryValue',
        },
        {
          text: 'Valor total inicial',
          align: 'center',
          value: 'totalValue',
        },
        {
          text: 'Valor total atual',
          align: 'center',
          value: 'currentTotalValue',
        },
        {
          text: 'Data entrada',
          align: 'center',
          value: 'entryDate',
        },
        {
          text: 'Usuário',
          align: 'center',
          value: 'registrationUser.name',
          sortable: false,
        },
        {
          text: 'Motivo',
          align: 'center',
          value: 'reason',
          sortable: false,
        },
        {
          text: '',
          align: 'center',
          value: 'actions',
          sortable: false,
        },
      ],
    }
  },

  methods: {
    formatDate(date) {
      return FormatDate.dateTime(date)
    },

    formatCurrency(value) {
      return value != null ? value.toInstLocale() : 0
    },

    applyFilter() {
      if (this.$refs.form.validate()) {
        this.list()
      }
    },

    attach(item) {
      this.entry = item
      this.attachKey++
      this.attachDialog = true
    },

    edit(item) {
      this.entry = item
      this.key = !this.key
      this.dialog = true
    },

    reset() {
      this.filter.page = 1
      this.filter.start = ''
      this.filter.final = ''
      this.list()
    },

    list() {
      this.filter.stockItemId = this.stockItemId

      this.apiService
        .post('stockEntry/list/paginate', this.filter)
        .then((response) => {
          this.entries = response.content.entities

          const pager = response.content.pager
          this.filter.page = pager.currentPage
          this.filter.pageSize = pager.pageSize
          this.filter.totalItems = pager.totalItems
          this.filter.totalPages = pager.totalPages
        })
    },
  },

  watch: {
    'filter.page': {
      deep: true,
      handler() {
        this.list()
      },
    },
    'filter.pageSize': {
      deep: true,
      handler() {
        this.list()
      },
    },
  },
}
</script>

<style scoped lang="scss">
.table-custom {
  &::v-deep .v-data-table__wrapper {
    thead {
      border: none !important;

      th {
        border: none !important;
        &:first-child {
          border-radius: 4px 0 0 4px;
        }

        &:last-child {
          border-radius: 0 4px 4px 0;
        }
      }
    }
  }
}
.pagination {
  margin: 1.5rem 0;
  background: $grey-1;
  border-radius: 4px;
  padding: 0.8rem 1.3rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  .item-pagination {
    flex: 1 1 100px;
    margin: 0.5rem 0;
    .btn {
      border-radius: 4px;
      background: #c7c9ca;
      font-weight: bold;
    }
    .registros {
      span {
        font-weight: 700;
        font-size: 12px;
        line-height: 16px;
        color: $grey-2;
      }
    }
  }
  .block-one {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .block-two {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    .pagination-item {
      &::v-deep .v-pagination {
        li {
          button {
            box-shadow: none !important;
            background: none !important;
            color: $grey-2;
            font-weight: 500;
          }

          .v-pagination__item--active {
            color: $black-1;
          }
        }
      }
    }
  }

  @media (max-width: 406px) {
    .block-one {
      justify-content: center;
    }
    .block-two {
      justify-content: center;
    }
  }
}
</style>
